import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import {
  PatientBehaviorModel,
  PatientBehaviorType,
  PatientModel,
  AlarmOption,
  BehaviorGoalModel
} from '../../../core/api/models';
import {
  StaffApiService,
  SupervisorApiService,
} from '../../../core/api/services';
import { SupervisorHelperService } from '../../supervisor.helper.service';
import { AlertService } from '../../../shared/services/alert.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { Utils } from '../../../core/utils';
// Import from shared config
import { Editor, editorConfig } from '../../../shared/ckeditor/ckeditor-config';
import { SelectItem } from 'primeng/api';
import { getAlarmOptions } from '../../../shared/utils/utils';
import { getAlarmSoundOptions } from '../../../shared/utils/utils';
import { SupervisorBaseComponent } from '../../supervisor-base.component';
import { AuthService } from '../../../auth';
import { PatientDataFacadeService } from '../../../core/services/patient-data-facade.service';

@Component({
  selector: 'app-add-behavior',
  templateUrl: './add-behavior.component.html',
  styleUrl: './add-behavior.component.css',
  standalone: false,
})
export class AddBehaviorComponent
  extends SupervisorBaseComponent {
  @Input() selectedPatient: PatientModel | null = null;
  @Output() close = new EventEmitter<boolean>();

  patientBehaviorTypes: any[] = [
    { value: PatientBehaviorType.Frequency, text: 'Frequency' },
    { value: PatientBehaviorType.Duration, text: 'Duration' },
    { value: PatientBehaviorType.Interval, text: 'Interval' },
    { value: PatientBehaviorType.RateTimer, text: 'Rate Timer' },
    { value: PatientBehaviorType.Intensity, text: 'Intensity' },
  ];

  typingTimer: number | undefined;

  // Using the shared Editor configuration
  public Editor = Editor;

  initialOperationalDefinition: string = '';

  // Using shared editor configuration
  public editorConfig = editorConfig;

  selectedAlarmOption = signal<number | null>(null);
  selectedAlarmSound = signal<number | null>(null);

  alarmOptions: SelectItem[] = getAlarmOptions();
  alarmSounds: SelectItem[] = getAlarmSoundOptions();
  saving = false;
  canEditBip = false;

  behaviorGoals: BehaviorGoalModel[] | undefined;


  constructor(
    protected override supervisorApiService: SupervisorApiService,
    protected override alertService: AlertService,
    protected override supervisorHelperService: SupervisorHelperService,
    protected override authService: AuthService,
    protected override staffApiService: StaffApiService,
    patientDataFacadeService: PatientDataFacadeService
  ) {
    super(
      supervisorApiService,
      alertService,
      supervisorHelperService,
      authService,
      staffApiService,
      patientDataFacadeService
    );
    // TOUR LOGIC
    this.behaviorForm
      .get('patientBehaviorName')
      ?.valueChanges.subscribe((value) => {
        if (this.typingTimer) {
          clearTimeout(this.typingTimer);
        }
        this.typingTimer = window.setTimeout(() => {
          const event = new Event('name-entered');
          document.getElementById('addBehaviorForm')?.dispatchEvent(event);
        }, 500);
      });

    this.behaviorForm.get('behaviorType')?.valueChanges.subscribe((value) => {
      if (value != null) {
        const event = new Event('behavior-type-selected');
        document.getElementById('addBehaviorForm')?.dispatchEvent(event);
        // dismiss the dropdown
        const dropdown = document.getElementById('behaviorTypeDropdown_list');
        if (dropdown) {
          dropdown.style.display = 'none';
        }
      }
    });
  }

  behaviorForm = new FormGroup({
    patientBehaviorName: new FormControl<string | null>(''),
    behaviorType: new FormControl<PatientBehaviorType>(
      PatientBehaviorType.Duration,
      [Validators.required]
    ),
    operationalDefinition: new FormControl<string | null>(''),
    interval: new FormControl<number | null>(null),
    intervalMinutes: new FormControl<number | null>(null),
    alarmOption: new FormControl<number>(AlarmOption.NoAlarm),
    alarmSoundOption: new FormControl<number | null>(null),
    BSP_BehaviorGoalID: new FormControl<number | null>(null),
    BehaviorGoal_Other: new FormControl<string | null>(null),
    intensityScaleMin: new FormControl<number | null>(1),
    intensityScaleMax: new FormControl<number | null>(5),
    intensityScaleValues: new FormControl<string | null>(null),

  });

  behaviorToEdit: PatientBehaviorModel | null = null;

  titleName: string = 'Add Behavior';
  actionName: string = 'Add';

  ngOnInit(): void {
    this.canEditBip = this.authService.canEditBip();
    this.behaviorToEdit = {
      PatientID: this.selectedPatient?.ID ?? 0,
      BehaviorType: PatientBehaviorType.Duration,
      OperationalDefinition: '',
    };
    this.loadBaseData(this.selectedPatient?.ID!, () => {
      this.behaviorGoals = this.supervisorHelperService
        .behaviorGoals()
        .filter((goal) => !!goal.Goal);

      if (
        this.behaviorGoals?.find((goal) => goal.Goal === 'Other') ==
        null
      ) {
        this.behaviorGoals.push({
          Goal: 'Other',
          Behavior: '',
        });
      }
    });
    // EDIT LOGIC
    if (this.supervisorHelperService.behaviorToEdit() != null) {
      this.titleName = 'Edit Behavior';
      this.actionName = 'Save';
      this.behaviorToEdit = this.supervisorHelperService.behaviorToEdit();

      this.behaviorForm.patchValue({
        patientBehaviorName: this.behaviorToEdit?.PatientBehaviorName ?? '',
        behaviorType:
          this.behaviorToEdit?.BehaviorType ?? PatientBehaviorType.Duration,
        operationalDefinition: this.behaviorToEdit?.OperationalDefinition ?? '',
        interval: this.behaviorToEdit?.Interval,
        intervalMinutes: this.behaviorToEdit?.Interval ? this.secondsToMinutes(this.behaviorToEdit?.Interval) : 1,
        alarmOption: this.behaviorToEdit?.AlarmOption ?? AlarmOption.NoAlarm,
        alarmSoundOption: this.behaviorToEdit?.AlarmSoundOption ?? null,
        BSP_BehaviorGoalID: this.behaviorToEdit?.BSP_BehaviorGoalID ?? null,
        intensityScaleMin: this.behaviorToEdit?.IntensityScaleMin ?? 1,
        intensityScaleMax: this.behaviorToEdit?.IntensityScaleMax ?? 5,
        intensityScaleValues: this.behaviorToEdit?.IntensityScaleValues ?? null,

      });

      this.initialOperationalDefinition = this.behaviorToEdit?.OperationalDefinition ?? '';

      this.onBehaviorTypeChange({ value: this.behaviorToEdit?.BehaviorType });

      if (this.isIntensityScaleLocked()) {
        this.behaviorForm.get('intensityScaleMin')?.disable();
        this.behaviorForm.get('intensityScaleMax')?.disable();
        this.behaviorForm.get('intensityScaleValues')?.disable();
      }
    }
  }

  onCancel() {
    this.close.emit(false);
  }

  onSubmit() {
    this.behaviorForm.markAllAsTouched();

    if (this.behaviorForm.invalid) {
      const errors = Utils.getFormErrors(this.behaviorForm);
      if (errors.length > 0) {
        this.alertService.error(errors.join(' \n'));
        return;
      }
    }

    this.saveBehavior();
  }

  onBehaviorTypeDropdownClicked() {
    const event = new Event('dropdown-clicked');
    document.getElementById('addBehaviorForm')?.dispatchEvent(event);
  }

  saveBehavior() {

    this.saving = true;
    // check for form errors and display proper error message
    if (this.behaviorForm.invalid) {
      const errors = Utils.getFormErrors(this.behaviorForm);
      if (errors.length > 0) {
        this.alertService.error(errors.join(' \n'));
        this.saving = false;
        return;
      }
    }

    const customScaleError = this.shouldDisplayIntensityScaleFields()
      ? Utils.intensityScaleCustomValuesError(this.behaviorForm.get('intensityScaleValues')?.value)
      : null;
    if (customScaleError) {
      this.alertService.error(customScaleError);
      this.saving = false;
      return;
    }

    if (this.behaviorForm.get('intervalMinutes')?.value != null) {
      this.behaviorForm.get('interval')?.setValue(this.minutesToSeconds(this.behaviorForm.get('intervalMinutes')?.value ?? 0));
    } else {
      this.behaviorForm.get('interval')?.setValue(null);
    }

    const {
      operationalDefinition: submittedOperationalDefinition,
      intensityScaleMin,
      intensityScaleMax,
      intensityScaleValues,
      ...formValue
    } = this.behaviorForm.getRawValue();
    const operationalDefinition = this.canEditBip
      ? submittedOperationalDefinition
      : this.behaviorToEdit?.OperationalDefinition;
    this.behaviorToEdit = {
      ...this.behaviorToEdit,
      ...formValue,
      OperationalDefinition: operationalDefinition,
      BehaviorType: this.behaviorForm.get('behaviorType')?.value ?? PatientBehaviorType.Duration,
      IntensityScaleMin: intensityScaleMin,
      IntensityScaleMax: intensityScaleMax,
      IntensityScaleValues: intensityScaleValues,
    };

    this.staffApiService
      .SavePatientBehavior({
        body: this.behaviorToEdit,
      })
      .subscribe(
        (behavior) => {
          console.log(behavior);
          this.alertService.success('Behavior saved successfully');
          this.close.emit(true);
          this.saving = false;
        },
        (error) => {
          console.error(error);
          this.alertService.error(
            'Error saving Behavior - ' +
            Utils.getErrorMessageFromErrorResponse(error)
          );
          this.close.emit(false);
          this.saving = false;
        }
      );
  }

  onOperationalDefinitionChange(event: any): void {
    if (this.canEditBip && event.editor) {
      const data = event.editor.getData();
      this.behaviorForm.get('operationalDefinition')?.setValue(data);
    }
  }

  shouldDisplayIntervalField() {
    const behaviorType = this.behaviorForm.get('behaviorType')?.value;
    return behaviorType === PatientBehaviorType.Interval || behaviorType === PatientBehaviorType.RateTimer;
  }

  shouldDisplayIntensityScaleFields() {
    return this.behaviorForm.get('behaviorType')?.value === PatientBehaviorType.Intensity;
  }

  isIntensityScaleLocked() {
    return !!this.behaviorToEdit?.ID && !!this.behaviorToEdit?.HasData;
  }

  minutesToSeconds(minutes: number) {
    return minutes * 60;
  }

  secondsToMinutes(seconds: number) {
    return seconds / 60;
  }

  shouldShowAlarmOptions() {
    const behaviorType = this.behaviorForm.get('behaviorType')?.value;
    return behaviorType === PatientBehaviorType.RateTimer || behaviorType === PatientBehaviorType.Interval;
  }

  playAlarmSound(event: any) {
    const selectedSoundToPlay = event.value;
    const audio = new Audio(`assets/sounds/alarm-${selectedSoundToPlay}.mp3`);
    audio.play();
    setTimeout(() => {
      audio.pause();
    }, 5000);
  }

  onBehaviorTypeChange(event: any) {
    const behaviorType = event.value;
    if (behaviorType === PatientBehaviorType.RateTimer || behaviorType === PatientBehaviorType.Interval) {
      this.behaviorForm.get('intervalMinutes')?.setValidators([Validators.min(0.1), Validators.required]);
    } else {
      this.behaviorForm.get('intervalMinutes')?.clearValidators();
    }
    this.behaviorForm.get('intervalMinutes')?.updateValueAndValidity();

    if (behaviorType === PatientBehaviorType.Intensity) {
      this.behaviorForm.get('intensityScaleMin')?.setValidators([Validators.required, Validators.min(0), Validators.max(100)]);
      this.behaviorForm.get('intensityScaleMax')?.setValidators([Validators.required, Validators.min(0), Validators.max(100)]);
      if (this.behaviorForm.get('intensityScaleMin')?.value == null) {
        this.behaviorForm.get('intensityScaleMin')?.setValue(1);
      }
      if (this.behaviorForm.get('intensityScaleMax')?.value == null) {
        this.behaviorForm.get('intensityScaleMax')?.setValue(5);
      }
    } else {
      this.behaviorForm.get('intensityScaleMin')?.clearValidators();
      this.behaviorForm.get('intensityScaleMax')?.clearValidators();
    }
    this.behaviorForm.get('intensityScaleMin')?.updateValueAndValidity();
    this.behaviorForm.get('intensityScaleMax')?.updateValueAndValidity();
  }

  selectedBehaviorGoal() {
    if (this.behaviorToEdit?.BSP_BehaviorGoalID) {
      return this.behaviorGoals?.find(
        (goal) => goal.ID === this.behaviorToEdit?.BSP_BehaviorGoalID
      );
    }
    return null;
  }
}
